import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AI_TOOLS_ITEMS } from './navConfig';

interface AiToolsSheetProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Bottom sheet listing every AI tool for quick selection. Slides up above the
 * bottom nav with NO backdrop (the nav stays visible below it). Selecting a
 * tool animates the sheet closed first, then navigates on exit-complete.
 */
export function AiToolsSheet({ open, onClose }: AiToolsSheetProps) {
  const navigate = useNavigate();
  const [pendingRoute, setPendingRoute] = useState<string | null>(null);

  const selectTool = (to: string) => {
    setPendingRoute(to);
    onClose(); // triggers the exit animation; navigation happens on exit-complete
  };

  return (
    <AnimatePresence
      onExitComplete={() => {
        if (pendingRoute) {
          navigate(pendingRoute);
          setPendingRoute(null);
        }
      }}
    >
      {open && (
        <motion.div
          key="ai-tools-sheet"
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', stiffness: 360, damping: 36, mass: 0.9 }}
          className="rounded-t-2xl border-t border-x border-gray-200 bg-white shadow-[0_-8px_30px_-8px_rgba(0,0,0,0.25)] dark:border-zinc-800 dark:bg-zinc-900"
          role="dialog"
          aria-label="AI tools"
        >
          {/* Grabber handle */}
          <div className="flex justify-center pt-2.5 pb-1">
            <span className="h-1 w-10 rounded-full bg-gray-300 dark:bg-zinc-700" />
          </div>

          <div className="px-4 pb-4">
            <p className="mb-2 px-1 text-[11px] font-semibold uppercase tracking-widest text-gray-400 dark:text-zinc-500">
              AI Tools
            </p>
            <div className="grid grid-cols-2 gap-2">
              {AI_TOOLS_ITEMS.map((item) => (
                <button
                  key={item.to}
                  onClick={() => selectTool(item.to)}
                  className="flex min-h-[52px] items-center gap-3 rounded-xl border border-gray-100 bg-gray-50 px-3 py-2.5 text-left text-sm font-medium text-gray-800 transition-colors active:scale-[0.98] hover:border-brand-200 hover:bg-brand-50 dark:border-zinc-800 dark:bg-zinc-800/50 dark:text-zinc-100 dark:hover:border-brand-900 dark:hover:bg-brand-950/30"
                >
                  <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-white text-brand-600 shadow-sm dark:bg-zinc-900 dark:text-brand-400">
                    {item.icon}
                  </span>
                  <span className="min-w-0 truncate">{item.label}</span>
                </button>
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
